import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, ReplaySubject, finalize, share } from 'rxjs';

export const PRODUCT_LOCKED_REASON = 'Another request on this product is still running.';
export const PRODUCTS_LOCKED_REASON = 'Another request on one or more of these products is still running.';

/** The proposal ids a project-detail payload writes, for `hold`. */
export function lockIds(detail: any): (string | undefined)[] {
  return (detail?.sites ?? []).map((siteDetail: any) => siteDetail?.proposal?.id);
}

// Locks are keyed on proposal (product) ids, so a write disables only the
// controls that act on one of the products it touches.
@Injectable({ providedIn: 'root' })
export class ProjectActionLockService {
  private readonly tokenCounts = new BehaviorSubject<ReadonlyMap<string, number>>(new Map());

  public hold<T>(
    proposalIds: readonly (string | null | undefined)[],
    request$: Observable<T>
  ): Observable<T> {
    const ids = [...new Set(proposalIds.filter((id): id is string => !!id))];
    if (ids.length) {
      const counts = new Map(this.tokenCounts.value);
      for (const id of ids) {
        counts.set(id, (counts.get(id) ?? 0) + 1);
      }
      this.tokenCounts.next(counts);
    }

    const shared = request$.pipe(
      finalize(() => {
        if (ids.length) {
          const counts = new Map(this.tokenCounts.value);
          for (const id of ids) {
            const remaining = (counts.get(id) ?? 1) - 1;
            if (remaining > 0) {
              counts.set(id, remaining);
            } else {
              counts.delete(id);
            }
          }
          this.tokenCounts.next(counts);
        }
      }),
      share({
        connector: () => new ReplaySubject<T>(1),
        resetOnError: false,
        resetOnComplete: false,
        resetOnRefCountZero: false,
      })
    );

    shared.subscribe({
      // Errors reach callers through the returned observable; do not rethrow here.
      error: (error) => console.error(error),
    });
    return shared;
  }

  public isLocked(proposalId: string | null | undefined): boolean {
    const counts = this.tokenCounts.value;
    if (counts.size === 0 || !proposalId) {
      return false;
    }
    return (counts.get(proposalId) ?? 0) > 0;
  }
}
